import { formatCurrencyAmount } from 'utils/currency';

const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export const computeBarMax = ({ actual, threshold, directionPreference }) => {
  const a = toNumber(actual);
  const t = toNumber(threshold);
  if (a == null && t == null) return 1;
  if (a == null) return Math.max(1, Math.abs(t || 1));
  if (t == null) return Math.max(1, Math.abs(a || 1));

  const max = Math.max(a, t);
  const dp = String(directionPreference || '').toLowerCase();
  if (dp === 'lower_is_better') return Math.max(1, max * 1.2);

  // higher_is_better | target_value (fallback to higher_is_better rule)
  if (t !== 0 && a / t > 5) return Math.max(1, t * 5);
  return Math.max(1, max * 1.05);
};

// For `ratio_pct`: input is a ratio (0–1), shown as a percentage (multiply ×100).
const formatPercentFromRatio = (ratio, decimals) => {
  const n = toNumber(ratio);
  if (n == null) return '—';
  return `${(n * 100).toLocaleString('es-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
};

// For `percentage_*`: input is already in percent units (e.g. 95 for 95%); no multiplication.
const formatPercent = (value, minDecimals, maxDecimals = minDecimals) => {
  const n = toNumber(value);
  if (n == null) return '—';
  return `${n.toLocaleString('es-ES', { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals })}%`;
};

// Max decimals each format_hint may render. Used to round numeric values so the
// progress bar plots the same number that the text shows (no "0% vs 0%" while
// the bar disagrees).
export const getDisplayMaxPrecision = (formatHint) => {
  const hint = String(formatHint || '').toLowerCase();
  if (hint === 'percentage_0d') return 2;
  if (hint === 'percentage_1d') return 1;
  if (hint === 'percentage_2d') return 2;
  if (hint === 'ratio_pct') return 1;
  if (hint === 'ratio' || hint === 'score_0_1') return 2;
  if (hint === 'decimal_1' || hint === 'time_seconds') return 1;
  if (hint === 'decimal_2' || hint === 'currency_decimal') return 2;
  return 0;
};

export const roundToDisplayPrecision = (value, formatHint) => {
  const n = toNumber(value);
  if (n == null) return null;
  const d = getDisplayMaxPrecision(formatHint);
  // `ratio_pct` stores a 0–1 ratio but renders it ×100, so its display decimals
  // count percent-points, not ratio digits. Round in that same ×100 space —
  // otherwise e.g. 0.5327 rounds to 0.5 and shows "50,0%" instead of "53,3%".
  const ratioScale = String(formatHint || '').toLowerCase() === 'ratio_pct' ? 100 : 1;
  const mul = Math.pow(10, d) * ratioScale;
  return Math.round(n * mul) / mul;
};

const formatMultiplier = (mult) => {
  const n = toNumber(mult);
  if (n == null) return null;
  return `${n.toLocaleString('es-ES', { maximumFractionDigits: 2 })}×`;
};

const normalizeWhitespace = (s) => String(s).replace(/\s+/g, ' ').trim();

const LEADING_OPERATOR_RE = /^\s*(≥|≤|<=|>=|<|>|=)\s*/;
const MULTIPLIER_REF_RE = /^(\d+(?:[.,]\d+)?)\s*×\s+(.+)$/;
const NUMERIC_PERCENT_PREFIX_RE = /^(-?\d+(?:[.,]\d+)?)\s*(%)(?:\s+(.*))?$/;

const parseNumericPercentPrefix = (label) => {
  const m = String(label).match(NUMERIC_PERCENT_PREFIX_RE);
  if (!m) return null;
  const num = Number(m[1].replace(',', '.'));
  if (!Number.isFinite(num)) return null;
  return { value: num, suffix: (m[3] || '').trim() };
};

// Some backend rule seeds restate the threshold inside `threshold_label` at the
// ×100 percent scale (e.g. "30%") even though the numeric value is the ratio
// (0.3) and the format hint renders without ×100 — producing "0,3% 30%". When
// the label's numeric prefix scales to the threshold value, treat it as a
// redundant restatement and keep only any meaningful suffix.
const isPercentScaledRestatement = (parsed, valueNum, formatHint) => {
  if (!parsed || !Number.isFinite(valueNum)) return false;
  const hint = String(formatHint || '').toLowerCase();
  if (!hint.startsWith('percentage') && hint !== 'ratio_pct') return false;
  const expected = valueNum * 100;
  const tol = 1e-6 * Math.max(1, Math.abs(expected));
  return Math.abs(parsed.value - expected) <= tol;
};

// TODO(ESPORAI-430): remove the legacy-shim branch once backend rule seeds
// stop embedding operators / formatted values into `threshold_label`.
export const formatThresholdVs = ({
  thresholdLabel,
  thresholdMultiplier,
  thresholdValue,
  formatHint,
  currency = 'USD',
}) => {
  const valueNum = toNumber(thresholdValue);
  if (valueNum == null) return null;

  const formattedValue = formatByHint(valueNum, formatHint, currency);

  let label = thresholdLabel ? String(thresholdLabel).trim() : null;
  let multiplier = toNumber(thresholdMultiplier);

  if (label) {
    label = label.replace(LEADING_OPERATOR_RE, '').trim();
    if (multiplier == null) {
      const m = label.match(MULTIPLIER_REF_RE);
      if (m) {
        multiplier = Number(m[1].replace(',', '.'));
        label = m[2].trim();
      }
    }
    if (label) {
      const normLabel = normalizeWhitespace(label);
      const normValue = normalizeWhitespace(formattedValue);
      if (normLabel === normValue) {
        label = null;
      } else if (normValue && normLabel.startsWith(`${normValue} `)) {
        label = normLabel.slice(normValue.length + 1).trim();
      } else {
        const parsed = parseNumericPercentPrefix(normLabel);
        if (isPercentScaledRestatement(parsed, valueNum, formatHint)) {
          label = parsed.suffix || null;
        }
      }
    }
  }

  if (label && multiplier != null) {
    // When the threshold value IS the multiplier (e.g. format_hint=ratio rules
    // where the metric itself is a ratio), the parenthetical just repeats the
    // multiplier — drop it.
    if (Math.abs(multiplier - valueNum) < 1e-9) {
      return `${formatMultiplier(multiplier)} ${label}`;
    }
    return `${formatMultiplier(multiplier)} ${label} (${formattedValue})`;
  }
  if (label) {
    return `${formattedValue} ${label}`;
  }
  return formattedValue;
};

export const formatByHint = (value, formatHint, currency = 'USD') => {
  const n = toNumber(value);
  const hint = String(formatHint || '').toLowerCase();
  if (n == null) return '—';

  if (hint === 'ratio') return `${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`;
  if (hint === 'ratio_pct') return formatPercentFromRatio(n, 1);
  if (hint === 'percentage_2d') return formatPercent(n, 2);
  if (hint === 'percentage_1d') return formatPercent(n, 1);
  if (hint === 'percentage_0d') return formatPercent(n, 0, 2);
  if (hint === 'integer') return n.toLocaleString('es-ES', { maximumFractionDigits: 0 });
  if (hint === 'integer_thousands') return n.toLocaleString('es-ES', { maximumFractionDigits: 0 });
  if (hint === 'decimal_1') return n.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (hint === 'decimal_2') return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (hint === 'score_0_1') return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (hint === 'time_seconds') return `${n.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}s`;
  if (hint === 'time_days') return `${n.toLocaleString('es-ES', { maximumFractionDigits: 0 })} días`;
  if (hint === 'currency') {
    const code = String(currency || 'USD').toUpperCase();
    const amount = n.toLocaleString('es-ES', { maximumFractionDigits: 0 });
    return `${code} ${amount}`;
  }
  if (hint === 'currency_decimal') return formatCurrencyAmount(n, currency, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

